import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomErrors } from '@asamco/base';
import { ParameterType as ASParameterType } from '@asamco/management-portal/shared';
import { FormHelpers, getErrorSwal, showErrorSwal } from '@asamco/utils';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { BehaviorSubject, Observable, of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApplicationLoginStatus } from '../../enums/application-login-status.enum';
import { ApplicationLoginType } from '../../enums/application-login-type.enum';
import { AuthorizationStatus } from '../../enums/authorization-status.enum';
import { LoginFieldType } from '../../enums/login-field-type.enum';
import { ParameterType as ALParameterType } from '../../enums/parameter-type.enum';
import { ApplicationParams } from '../../interfaces/application-login-params.interface';
import { ApplicationLogin } from '../../interfaces/application-login.interface';
import { LoginField } from '../../interfaces/login-field.interface';
import { RawLoginPackage } from '../../interfaces/raw-login-package.interface';
import { ApplicationLoginParamsService } from '../../services/application-login-params.service';
import { ApplicationLoginService } from '../../services/application-login.service';
import { AuthFlowModalComponent } from '@asamco/application-link/shared';
import { AuthFlowOutput } from '@asamco/application-link/shared';
import { DxiButtonModule } from 'devextreme-angular/ui/nested';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTextBoxModule, DxNumberBoxModule, DxSelectBoxModule, DxCheckBoxModule, DxSwitchModule } from 'devextreme-angular';
import { NgIf, NgFor, NgSwitch, NgSwitchCase, NgClass, AsyncPipe, JsonPipe } from '@angular/common';
import { AuthFlowModalComponent as AuthFlowModalComponent_1 } from '../../../../../shared/src/lib/components/auth-flow-modal/auth-flow-modal.component';
import { ApplicationTypeParameterLogin } from '../../interfaces/application-type-param-login.interface';
import { LinkEditApplicationLoginFieldsComponent } from './link-edit-application-login-fields/link-edit-application-login-fields.component';

@Component({
    selector: 'al-link-edit-application-login',
    templateUrl: './link-edit-application-login.component.html',
    styleUrls: ['./link-edit-application-login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AuthFlowModalComponent_1,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        DxTextBoxModule,
        AsValidatorDirective,
        DxiButtonModule,
        DxNumberBoxModule,
        DxSelectBoxModule,
        NgSwitch,
        NgSwitchCase,
        NgClass,
        DxCheckBoxModule,
        AsyncPipe,
		DxSwitchModule,
		LinkEditApplicationLoginFieldsComponent,
		JsonPipe
    ],
})
export class LinkEditApplicationLoginComponent implements OnInit, OnDestroy {
	@Output() reloadAppLoginDatasource = new EventEmitter<string>();
	@Output() notifyAccountFound = new EventEmitter<RawLoginPackage>();

	@ViewChild(AuthFlowModalComponent)
	authFlowModal: AuthFlowModalComponent;

	@Input()
	formGroup: FormGroup;

	@Input()
	loginType: ApplicationLoginType;

	@Input()
	testerMode: boolean;

	@Input()
	applicationId: number;

	@Input()
	fromLinkType: string;

	@Input()
	initLogin: ApplicationLogin;

	@Input()
	linkId: number;

	//Either to or from
	@Input()
	appSide: string;

	@Input()
	public appParams: ApplicationParams;

	appLoginStatus: ApplicationLoginStatus = ApplicationLoginStatus.undefined;
	loginFieldType = LoginFieldType;
	loginFields: LoginField[];

	leadingOrTrailingSpace: { [control: string]: boolean } = {}; // Flag to track leading or trailing space


	toolTipVisible: boolean = false;

	isEncrypted: boolean = false;

	/** New auth generator */
	private parameterValidators: any[] = [];
	/** Forms for validation */

	private stopPolling$ = new Subject();

	public readonly asParameterType = ASParameterType;
	public readonly alParameterType = ALParameterType;

	public customErrors: CustomErrors = {
		ERROR_REQUIRED: 'This field is required',
		ERROR_MAXLENGTH: 'This field exceeds the maximum allowed amount of characters',
	};

	/** Boolean for indicating when the form is initialized */
	public loginValiditySubject: BehaviorSubject<ApplicationLoginStatus> = new BehaviorSubject<ApplicationLoginStatus>(
		ApplicationLoginStatus.undefined
	);
	public doesLoginHaveId: Subject<boolean> = new BehaviorSubject<boolean>(true);
	public doesLoginHaveId$: Observable<boolean> = this.doesLoginHaveId.asObservable();

	disabledLoginBtnSubject = new Subject<boolean>();
	disabledLoginBtnObservable = this.disabledLoginBtnSubject.asObservable();

	private destroy$: Subject<void> = new Subject<void>();

	public readonly passwordTextBoxMode$ = new BehaviorSubject<'text' | 'password'>('password');
	public readonly passwordIcon$ = new BehaviorSubject<'far fa-eye' | 'far fa-eye-slash'>('far fa-eye');

	public passwordButtonOptions = {
		type: 'as-password-toggle',
		icon: this.passwordIcon$.value,
		onClick: () => {
			this.passwordIcon$.next(this.passwordIcon$.value === 'far fa-eye' ? 'far fa-eye-slash' : 'far fa-eye');
			this.passwordTextBoxMode$.next(this.passwordTextBoxMode$.value === 'text' ? 'password' : 'text');
		},
	};

	constructor(
		@Inject(L10N_LOCALE) public locale: L10nLocale,
		private appLoginService: ApplicationLoginService,
		private cdr: ChangeDetectorRef
	) {}

	public onPasswordToggleClicked($event) {}

	public get applicationLoginStatus(): typeof ApplicationLoginStatus {
		return ApplicationLoginStatus;
	}

	ngOnInit(): void {
		//this.InitFormGroup();
		if(this.formGroup)
		{
			if(this.appParams?.parameters) {
				this.initializeParameterForm(this.appParams.parameters);
			}
			this.checkFormGroupForLeadingOrTrailingSpace();
		}

		if (this.initLogin) this.setLogin(this.initLogin);
	}

	parseBoolean(value: string): boolean {
		return value.toLowerCase() === 'true' || value === '1';
	  }

	//Integration tester alternative from test login, this one also pushes the result to the integration tester if valid
	retrieveCredentials() {
		if (FormHelpers.validateForm(this.formGroup)) {
			this.disabledLoginBtnSubject.next(true);
			const login: ApplicationLogin = this.formGroup.value;
			login.applicationId = this.applicationId;
			if (!login.id) login.id = 0;

			this.appLoginService.testLoginRaw(login, this.isEncrypted).subscribe({
				next: result => {
					this.disabledLoginBtnSubject.next(false);
					if (result) {
						let items: RawLoginPackage = { applicationLogin: login, isEncrypted: this.isEncrypted };
						//Call back to the parent component to inform that we found a proper applicationlogin
						this.notifyAccountFound.emit(items);
					} else {
						this.notifyAccountFound.emit(undefined);
					}
				},
				error: error => this.disabledLoginBtnSubject.next(false)
			});
		}
	}

	switchIndex: number = 0;
	initializeParameterForm(parameters: Array<ApplicationTypeParameterLogin>) {
		for (let param of parameters) {
			const validators = [];
			if(param.underlyingElements && param.underlyingElements.length > 0)
			{
				this.switchIndex++;
				param.fieldName = 'Switchboxfield' + this.switchIndex;
			}

			if (param.isRequired && param.type !== ASParameterType.Password && param.type !== ASParameterType.ConnectionString) {
				validators.push(Validators.required);
			}
			if (param.type === ASParameterType.Email) {
				//	validators.push(Validators.email);
			}

			//The 'Name' field is a standard fillable field, but doesn't require validation/checking
			if(param.name !== 'Name')
			{
				this.parameterValidators.push(param.fieldName);
			}

			this.leadingOrTrailingSpace[param.fieldName] = false; // Initialize flag for each control
			if (param.defaultValue) {
			{
				this.formGroup.addControl(param.fieldName, new FormControl(param.defaultValue, { validators: validators })); //param.defaultValue
			}
			} else {
				this.formGroup.addControl(param.fieldName, new FormControl('', { validators: validators }));
			}

			//Resolve underlying elements
			if(param.underlyingElements)
			{
				this.initializeParameterForm(param.underlyingElements);

				this.formGroup.get(param.fieldName).valueChanges.pipe(distinctUntilChanged()).subscribe((value: boolean) => {
					//If we're displaying the underlying elements..
					if(value)
					{
						for(let element of param.underlyingElements)
						{
							this.formGroup.get(element.fieldName).enable();
						}
					}
					else
					{
						for(let element of param.underlyingElements)
						{
							this.formGroup.get(element.fieldName).disable();
						}
					}
					this.cdr.detectChanges();
				  });
			}
		}
	}

	checkFormGroupForLeadingOrTrailingSpace() {
		this.parameterValidators.forEach(controlName => {
			if(controlName)
			{
				this.formGroup.get(controlName).valueChanges.subscribe(value => {
				  if (typeof value === 'string') {
					const trimmedValue = value.trim();
					this.leadingOrTrailingSpace[controlName] = value !== trimmedValue || (trimmedValue === '' && value !== '');
				  } else {
					this.leadingOrTrailingSpace[controlName] = false;
				  }
				});
			}
		  });

	}


	hasLeadingOrTrailingSpace(controlName: string): boolean {
		return this.leadingOrTrailingSpace[controlName];
	  }

	toggleIconTooltip(e) {
		e.preventDefault();
		this.toolTipVisible = !this.toolTipVisible;
	}

	dropDownValueChanged(event) {
		console.log(event);
	}

	/**
	 * Validates the form and returns the result.
	 * Returns null if not valid.
	 */
	public save(): object {
		const result = {};
		if (FormHelpers.validateForm(this.formGroup, result)) {
			return this.formGroup.get('id').value;
		}

		return null;
	}

	getProperty(prop, obj) {
		prop = (prop + '').toLowerCase();
		for (const p in obj) {
			if (obj.hasOwnProperty(p) && prop === (p + '').toLowerCase()) {
				return obj[p];
			}
		}
	}

	public setLogin(login: ApplicationLogin): void {
		this.disabledLoginBtnSubject.next(true);
		if (login && login.id > 0) {
			this.doesLoginHaveId.next(true);
		} else {
			this.doesLoginHaveId.next(false);
		}
		if (login && this.formGroup) {
			// tslint:disable-next-line:forin
			for (const controlName in this.formGroup.controls) {
				//Case-insensitive search, since property search is otherwise always case-sensitive
				let value = this.getProperty(controlName, login);
				const controller = this.formGroup.get(controlName);

				if(controlName.includes('Switchboxfield'))
				{
					//check if the child entities of this entity not default, if so, enable the parent
					let underlyingElements = this.appParams.parameters.find(e => e.fieldName === controlName).underlyingElements;
					if(underlyingElements && underlyingElements.length > 0)
					{
						//we loop over the underlying elements, if one of them does not have a default value, we enable the parent
						let isFieldEnabled = false;
						underlyingElements.forEach(e => {
							let value = this.getProperty(e.fieldName, login);
							//let childValue = this.formGroup.get(e.fieldName).value;

							if(value != null && e.defaultValue != value)
							{
								isFieldEnabled = true;
							}
						});

						controller.setValue(isFieldEnabled);
					}
				}
				else
				{
					controller.setValue(value);
				}

			}


			this.formGroup.get('id').setValue(login.id);
			this.formGroup.get('applicationId').setValue(this.applicationId);
		} else if (this.formGroup) {
			this.formGroup.reset();
			//Reset values to default applicationparams
			this.appParams.parameters.forEach(e => {
				let field = this.formGroup.get(e.fieldName);
				if (field && e.defaultValue) {
					if(e.fieldName.includes('Switchboxfield'))
					{
						field.setValue(e.defaultValue.toLowerCase() === "true");
					}
					else
					{
						field.setValue(e.defaultValue);
					}

				}
			});

			this.formGroup.get('id').setValue(0);
			this.formGroup.get('applicationId').setValue(this.applicationId);
		}
		this.disabledLoginBtnSubject.next(false);
	}

	public testbtn(): void {
		if (FormHelpers.validateForm(this.formGroup)) {
			this.disabledLoginBtnSubject.next(true);
			var login: ApplicationLogin = this.formGroup.value;
			if (!login.id) login.id = 999999999;
			this.loginValiditySubject.next(ApplicationLoginStatus.pending);
			this.appLoginService.testLogin(this.linkId, this.appSide, login, this.fromLinkType.toLowerCase()).subscribe(
				result => {
					this.disabledLoginBtnSubject.next(false);
					if (result) {
						//this.formGroup.get('id').setValue(result.id); //Seperate the save & test function, because otherwise the "save" function is useless
						this.loginValiditySubject.next(ApplicationLoginStatus.valid);
					} else {
						Swal.fire({ title: 'Unable to authenticate', icon: 'error' });
						this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
					}
				},
				error => {
					this.disabledLoginBtnSubject.next(false);
					this.loginValiditySubject.next(ApplicationLoginStatus.invalid);

					const errorSwal = getErrorSwal(error);

					const authFlowRequiredText = 'Auth flow step required: ';
					if (errorSwal.text.startsWith(authFlowRequiredText)) {
						// remove authFlowRequiredText and ending dot
						const substr = errorSwal.text.substring(authFlowRequiredText.length, errorSwal.text.length - 1);
						const afo = JSON.parse(substr);

						this.authFlowModalStep(login, afo).subscribe(
							authFlowOutput => {
								if(this.loginValiditySubject.value !== ApplicationLoginStatus.valid) this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
								this.disabledLoginBtnSubject.next(false);
							},
							err => {
								showErrorSwal(err);
								this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
								this.disabledLoginBtnSubject.next(false);
							}
						);
					} else {
						Swal.fire(errorSwal);
					}
				}
			);
		}
	}

	public authorizebtn(): void {
		this.disabledLoginBtnSubject.next(true);
		const login: ApplicationLogin = this.formGroup.value;
		if (!login.id) login.id = 0;

		this.appLoginService
			.authorize(login, this.linkId, this.appSide, this.formGroup?.controls?.Dropdown?.value)
			.pipe(
				catchError(error => {
					showErrorSwal(error);
					return of(error);
				}),
				switchMap(authResult => {
					//If error => value should exist, otherwise not
					if (authResult.ok === false) {
						//If the result isn't correct, we return an empty observable to cancel the map
						return of([]);
					}

					//We created a new ApplicationLogin in the back-end, in order to make sure that the front-end doesn't create new logins we assign the value here
					this.formGroup.get('id').setValue(authResult.applicationLoginId);

					window.open(authResult.redirectUrl, '_blank');
					return timer(0, 3000).pipe(
						switchMap(() => {
							return this.appLoginService.pollAuthorizeStatus(authResult.state).pipe(takeUntil(this.stopPolling$));
						}),
						takeUntil(this.stopPolling$)
					);
				})
			)
			.subscribe({
				next: e => {
					//Result should be true if the user is succesfully authenticated
					switch (e) {
						case AuthorizationStatus.Succesful:
							this.stopPolling$.next(undefined);
							Swal.fire({
								icon: 'success',
								title: 'Succesfully authenticated',
							});
							this.disabledLoginBtnSubject.next(false);
							break;
						case AuthorizationStatus.Error:
							this.stopPolling$.next(undefined);
							Swal.fire({
								icon: 'error',
								text: 'Error authenticating user',
							});
							this.disabledLoginBtnSubject.next(false);
							break;
						case AuthorizationStatus.Connecting:
							break;
						default:
							this.disabledLoginBtnSubject.next(false);
							break;
					}
				},
				error: err => {
					showErrorSwal(err);
					this.disabledLoginBtnSubject.next(false);
				}
			});
	}

	private authFlowStep(login, afInput) {
		return this.appLoginService.authFlow(login, afInput).pipe(switchMap(afo => this.authFlowModalStep(login, afo)));
	}

	private authFlowModalStep(login, authFlowOutput: AuthFlowOutput) {
		if (authFlowOutput.state === 'Successful') this.loginValiditySubject.next(ApplicationLoginStatus.valid);
		else if(authFlowOutput.state === 'Failed') this.loginValiditySubject.next(ApplicationLoginStatus.invalid);
		return this.authFlowModal
			.show(authFlowOutput)
			.pipe(
				switchMap(authFlowInput => {
					if(authFlowInput.selectedOption === 'Finish') {
						return of([]);
					} else {
						return this.authFlowStep(login, authFlowInput);
					}
				})
			);
	}

	public savebtn(): void {
		if (FormHelpers.validateForm(this.formGroup)) {
			this.disabledLoginBtnSubject.next(true);


			//If we have a parameter with underlying elements, we'll need to check if that parameter is enabled/visible.
			//If it's enabled, we'll need to include the underlying elements in the login object, otherwise we'll need to remove them.
			//This is because the underlying elements are only visible when the parent parameter is enabled.

			const login: ApplicationLogin = this.formGroup.value;
			login.applicationId = this.applicationId;
			if (!login.id) login.id = 0;

			if (login.id > 0) {
				this.appLoginService.updateExistingLogin(this.linkId, this.appSide, login).subscribe({
					next: result => {
						if (result.success) {
							Swal.fire({
								icon: 'success',
								text: 'Successfully updated login',
							});
						}
						this.disabledLoginBtnSubject.next(false);
					},
					error: error => {
						showErrorSwal(error);
						this.disabledLoginBtnSubject.next(false);
					}
				});
			} else {
				this.appLoginService.createNewLogin(this.linkId, this.appSide, login).subscribe({
					next: result => {
						if (result.success) {
							this.formGroup.get('id').setValue(result.id);
							this.doesLoginHaveId.next(true);
							this.reloadAppLoginDatasource.emit(this.appSide);
							Swal.fire({
								icon: 'success',
								text: 'Successfully created login',
							});
						}
						this.disabledLoginBtnSubject.next(false);
					},
					error: error => {
						showErrorSwal(error);
						this.disabledLoginBtnSubject.next(false);
					}
				});
			}
		}
	}

	ngOnDestroy(): void {
		this.stopPolling$.next(undefined);
		this.stopPolling$.complete();

		this.destroy$.next();
		this.destroy$.complete();
	}
}
